/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C3a §4] THE WORDS FOR THE ARRANGEMENT VIEW.
 *
 *  ⚠️ THE ARCHIVE VOCABULARY IS NOT HERE. It lives in `components/archive/copy.ts`, shared with
 *  the detail page and the arrangements list, because all three drive the same flow — §8's own
 *  reason. A fourth copy of "Withdraw this order?" is a fourth thing to keep in step.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const VIEW_COPY = {
  edit: 'Edit',
  /**
   * ⚠️ [C3b §3.2] THE CARD'S NUMBER, AND IT IS THE ONE ON THE PAPER. `ArrangementPreviewModal`
   * renders `DP{displayOrder + 1}` and the printed sheet numbers from
   * `orderSheetDutyPointSchema.ordinal`, so this is a third renderer of one fact rather than a
   * new vocabulary — which is why it is derived from `displayOrder` and never from an array index.
   */
  dutyPointOrdinal: (n: number): string => `DP${String(n)}`,
  /**
   * ⚠️ [C3a §4a] An archived order is not editable — `updateArrangement` refuses — so the control
   * is disabled and this says which of the three possible refusals applies. It names the STATE of
   * the ORDER and what to do about it, never the caller.
   */
  editArchived: 'This order is in the Archive. Restore it before making changes.',
  /*
   * ⚠️ [C5b §3] `archivedPill` MOVED TO `lib/labels.ts` (`ARRANGEMENT_ARCHIVED_LABEL`), read through
   * `orderStatusWord`. Three pages spelt the archive two ways — `Archived` here, `In the Archive` on
   * the preview and the old record page — and one word read by one function is D9's fix.
   */
  print: 'Print order',
  publish: 'Publish',
  nameOfficers: 'Name officers',

  /** ⚠️ The parent breadcrumb is a REAL `<a>` per `NameCell`'s rule — never `history.back()`. */
  parent: 'Manage Arrangements',

  orderDate: 'Order date',
  dutyDates: 'Duty dates',
  divisions: 'Divisions',
  filled: 'Filled',
  /** [C5c §5a] Prefixes `presenceLine`, as the preview's own line does. */
  attendance: 'Attendance',

  /**
   * ⚠️ [C3a §4d] THE ACCORDION HEADING SAYS WHICH NIGHT IT IS SHOWING, BECAUSE IT IS SHOWING ONE.
   *
   * `?view=preview` resolves the nearest upcoming duty per card and echoes it — the client never
   * re-derives "nearest upcoming", because two expressions of that disagree at midnight
   * (`dutyDateResolved`'s rule). Where the card has more than one duty, saying so is what stops
   * an operator reading one night's grid as the whole order.
   */
  ofDuties: (count: number): string => `showing 1 of ${String(count)} duties`,
  /**
   * ⚠️ [C5 §8a/§8b] TWO SENTENCES, BECAUSE THEY ARE TWO FACTS.
   *
   * The objective one is what the duty point LACKS and reads the same for everybody; the
   * personal one is what THIS VIEWER still owes and appears only when they owe something. A
   * division that delegated everything sees the first and not the second, which is [UX1 §3]'s
   * settled rule and the whole reason they are not one number.
   *
   * ⚠️ `yours` SAYS `yours` RATHER THAN NAMING THE UNIT. The row already sits inside a page the
   * viewer opened with their own credentials, and naming their division would be longer and no
   * more precise — and on a super admin's screen it is absent entirely rather than wrong.
   */
  stillToName: (count: number): string => `${String(count)} still to name`,
  yoursToName: (count: number): string => `${String(count)} yours`,

  noDuty: 'No duties yet — this order has not been published.',
  noForce: 'No force has been asked for at this duty point.',
  noPoints: 'This order has no duty points.',
  noDivisions: 'No division has been asked for anything yet.',

  /**
   * ⚠️ [C3a §4a] A REFUSED OR BLOCKED ARCHIVE RENDERS A SENTENCE WHERE THE BUTTON WOULD BE.
   *
   * §4a says do not render an action that will refuse. The alternative to a refusing button is
   * not silence — an operator who cannot see why is left with a page that simply lacks a control
   * everybody told them about. `ARCHIVE_COPY` already owns the four sentences; this only names
   * where they go.
   */
  archiveUnavailable: 'archive-unavailable',

  /** [P7B §6b] The revert banner — a super admin, never a name; the instant in IST. */
  reverted: (at: string, reason: string): string => `Taken back to draft by a super admin on ${at}. Reason: ${reason}`,
} as const;
